import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Person as PersonIcon,
  School as SchoolIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Group as GroupIcon,
  Star as StarIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { userService } from '../../services/userService';
import { availabilityService } from '../../services/availabilityService';
import { robojacketsTheme } from '../../theme/robojacketsTheme';

const TeamSeeking = () => {
  const [users, setUsers] = useState([]);
  const [userAvailabilities, setUserAvailabilities] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTeamSeekingUsers();
  }, []);

  const loadTeamSeekingUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getTeamSeekingUsersWithGroups();
      setUsers(data);
      
      // Debug: Log the first user's data structure
      if (data.length > 0) {
        console.log('Sample user data:', data[0]);
        if (data[0].availabilities) {
          console.log('Sample availability data:', data[0].availabilities);
          console.log('Sample availability structure:', data[0].availabilities[0]);
        }
      }
      
      // Extract availability data from user objects
      const availabilityMap = {};
      data.forEach(user => {
        if (user.availabilities && user.availabilities.length > 0) {
          // Filter out invalid availability slots - backend uses startTime instead of timeSlot
          const validAvailabilities = user.availabilities.filter(slot => 
            slot && slot.dayOfWeek && slot.startTime
          );
          availabilityMap[user.id] = validAvailabilities;
          
          // Log any invalid slots for debugging
          if (validAvailabilities.length !== user.availabilities.length) {
            console.warn(`User ${user.id} has ${user.availabilities.length - validAvailabilities.length} invalid availability slots`);
            console.warn('Invalid slots:', user.availabilities.filter(slot => !slot || !slot.dayOfWeek || !slot.startTime));
          }
        } else {
          availabilityMap[user.id] = [];
        }
      });
      setUserAvailabilities(availabilityMap);
      
      setError(null);
    } catch (err) {
      setError('Failed to load team seeking users: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getDisplayName = (user) => {
    if (!user) return 'Unknown User';
    
    const name = user.preferredName || user.firstName || 'Unknown';
    const lastNameInitial = user.lastName ? user.lastName.substring(0, 1) : '';
    const graduationYear = user.graduationYear || '';
    return `${name} ${lastNameInitial}. ${graduationYear}`;
  };

  const getLeagueGroups = (groupMemberships) => {
    if (!groupMemberships) return [];
    return groupMemberships.filter(membership => 
      membership.groupTypeName && membership.groupTypeName.includes('League')
    );
  };

  const getStudentGroups = (groupMemberships) => {
    if (!groupMemberships) return [];
    return groupMemberships.filter(membership => 
      membership.groupTypeName && membership.groupTypeName.includes('Student')
    );
  };

  const formatAvailabilitySummary = (userId) => {
    const userAvailability = userAvailabilities[userId] || [];
    if (userAvailability.length === 0) {
      return 'No availability set';
    }
    
    // Create individual time slot chips
    const timeSlots = userAvailability.map(slot => {
      // Add null checks for required fields
      if (!slot || typeof slot !== 'object') {
        console.warn('Invalid availability slot object:', slot);
        return 'Invalid time slot';
      }
      
      if (!slot.dayOfWeek || !slot.startTime) {
        console.warn('Invalid availability slot:', slot);
        return 'Invalid time slot';
      }
      
      try {
        const dayName = slot.dayOfWeek.charAt(0) + slot.dayOfWeek.slice(1).toLowerCase();
        let timeFormatted;
        
        // Convert LocalTime to string format and handle AM/PM
        const timeString = slot.startTime.toString();
        
        if (timeString === '15:15') timeFormatted = '3:15 PM';
        else if (timeString === '18:00') timeFormatted = '6:00 PM';
        else if (timeString.startsWith('12:')) timeFormatted = timeString + ' PM';
        else if (timeString.startsWith('13:')) timeFormatted = (parseInt(timeString) - 12) + ':00 PM';
        else if (timeString.startsWith('14:')) timeFormatted = (parseInt(timeString) - 12) + ':00 PM';
        else if (timeString.startsWith('15:')) timeFormatted = (parseInt(timeString) - 12) + ':00 PM';
        else if (timeString.startsWith('16:')) timeFormatted = (parseInt(timeString) - 12) + ':00 PM';
        else if (timeString.startsWith('17:')) timeFormatted = (parseInt(timeString) - 12) + ':00 PM';
        else if (timeString.startsWith('18:')) timeFormatted = (parseInt(timeString) - 12) + ':00 PM';
        else if (timeString.startsWith('19:')) timeFormatted = (parseInt(timeString) - 12) + ':00 PM';
        else if (timeString.startsWith('20:')) timeFormatted = (parseInt(timeString) - 12) + ':00 PM';
        else timeFormatted = timeString + ' AM';
        
        // Add location if available
        const location = slot.locationName || 'Unknown Location';
        return `${dayName} ${timeFormatted} at ${location}`;
      } catch (error) {
        console.error('Error formatting availability slot:', error, slot);
        return 'Error formatting time slot';
      }
    });
    
    return timeSlots;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (users.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom color="primary">
          Team Seeking
        </Typography>
        <Alert severity="info">
          No students are currently seeking teams.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="primary">
        Team Seeking
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Students seeking team opportunities and their current league/student group memberships.
      </Typography>

      <Grid container spacing={3}>
        {users.map((user) => (
          <Grid item xs={12} md={6} lg={4} key={user.id}>
            <Card 
              elevation={2}
              sx={{ 
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                }
              }}
            >
              <CardContent>
                {/* User Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PersonIcon sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6" component="div">
                    {getDisplayName(user)}
                  </Typography>
                  {user.seekingTeam && (
                    <Chip 
                      label="Seeking Team" 
                      color="primary" 
                      size="small" 
                      sx={{ ml: 'auto' }}
                    />
                  )}
                </Box>

                {/* User Details */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <SchoolIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {user.school || 'School not specified'}
                    </Typography>
                  </Box>
                  
                  {user.graduationYear && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Graduating: {user.graduationYear}
                      </Typography>
                    </Box>
                  )}

                  {user.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EmailIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                  )}

                  {user.phoneNumber && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        {user.phoneNumber}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* League Groups */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    League Memberships
                  </Typography>
                  {getLeagueGroups(user.groupMemberships).length > 0 ? (
                    getLeagueGroups(user.groupMemberships).map((membership) => (
                      <Box key={membership.groupId} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <GroupIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                        <Typography variant="body2">
                          {membership.groupName}
                        </Typography>
                        {membership.isPrimaryUser && (
                          <Tooltip title="Primary User">
                            <StarIcon sx={{ fontSize: 16, color: 'warning.main', ml: 1 }} />
                          </Tooltip>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No league memberships
                    </Typography>
                  )}
                </Box>

                {/* Availability Information */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ScheduleIcon sx={{ fontSize: 16 }} />
                    Availability
                  </Typography>

                  {(() => {
                    const availabilitySlots = formatAvailabilitySummary(user.id);
                    if (availabilitySlots === 'No availability set') {
                      return (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {availabilitySlots}
                        </Typography>
                      );
                    }
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {availabilitySlots.map((slot, index) => {
                          const userAvailability = userAvailabilities[user.id] || [];
                          const slotData = userAvailability[index];
                          const isAvailable = slotData && slotData.isAvailable;
                          
                          // Only show available time slots
                          if (!isAvailable) return null;
                          
                          return (
                            <Chip
                              key={index}
                              label={slot}
                              color="primary"
                              size="small"
                              variant="outlined"
                            />
                          );
                        })}
                      </Box>
                    );
                  })()}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default TeamSeeking; 